"use client";

import { Routes } from "@/api";
import { seo } from "@/config/seo";
import { GtmService } from "@/services/api/gtm-service";
import { FooterConfig } from "@/types";
import {
  FileWarningIcon,
  LockKeyholeIcon,
  MailIcon,
  NewspaperIcon,
  NotebookTextIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { DiscordIcon } from "./ui/icons/discord-icon";
import { XIcon } from "./ui/icons/x-icon";

const HIDE_FOOTER_PATHS = [Routes.INBOX];

export function Footer() {
  const path = usePathname() ?? "no-path";
  const t = useTranslations("Footer");
  const tAddress = useTranslations("Contact.address.info");
  const tSocials = useTranslations("Contact.socials");

  const onListItemClick = (url: string) => () => {
    GtmService.push({
      event: "click",
      eventCategory: "link",
      eventLabel: "link-footer",
      eventAction: url,
    });
  };

  if (HIDE_FOOTER_PATHS.find((hidePath) => path.includes(hidePath))) {
    return null;
  }

  const footerConfig: FooterConfig = {
    company: {
      name: tAddress("name"),
      address: tAddress("address"),
      country: tAddress("country"),
    },
    columns: [
      {
        title: t("columns.legal.title"),
        items: [
          {
            title: t("columns.legal.disclaimer"),
            href: Routes.DISCLAIMER,
            icon: FileWarningIcon,
          },
          {
            title: t("columns.legal.privacyPolicy"),
            href: Routes.PRIVACY,
            icon: LockKeyholeIcon,
          },
          // {
          //   title: t("columns.legal.refundPolicy"),
          //   href: Routes.REFUND,
          //   icon: ReceiptTextIcon,
          // },
          {
            title: t("columns.legal.termsOfService"),
            href: Routes.TERMS,
            icon: NotebookTextIcon,
          },
        ],
      },
      {
        title: t("columns.resources.title"),
        items: [
          {
            title: t("columns.resources.about"),
            href: Routes.ABOUT,
          },
          {
            title: t("columns.resources.blog"),
            href: Routes.BLOG,
            icon: NewspaperIcon,
          },
        ],
      },
      {
        title: tSocials("title"),
        items: [
          // {
          //   title: tSocials("instagram"),
          //   href: seo.socials.instagram,
          //   icon: InstagramIcon,
          //   external: true,
          // },
          // {
          //   title: tSocials("tiktok"),
          //   href: seo.socials.tiktok,
          //   icon: TikTokIcon,
          //   external: true,
          // },
          {
            title: tSocials("discord"),
            href: seo.socials.discord,
            icon: DiscordIcon,
            external: true,
          },
          {
            title: tSocials("twitter"),
            href: seo.socials.x,
            icon: XIcon,
            external: true,
          },
        ],
      },
      {
        title: t("columns.resources.support.title"),
        items: [
          {
            title: t("columns.resources.support.contactUs"),
            href: Routes.CONTACT,
            icon: MailIcon,
          },
        ],
      },
    ],
  };

  return (
    <nav className="relative bottom-0 flex w-full flex-col border-t">
      <section className="flex min-h-36 flex-wrap items-start justify-between gap-3 p-2 pb-4 md:container">
        {footerConfig.columns.map((column) => (
          <div key={column.title} className="flex min-w-[120px] flex-col gap-1">
            <h3 className="text-lg font-bold text-foreground">
              {column.title}
            </h3>
            {column.items.map((item) =>
              item.external ? (
                <a
                  key={item.href}
                  href={item.href}
                  className="link-footer text-sm text-muted-foreground transition-colors duration-300 ease-in-out hover:text-foreground"
                  target="_blank"
                  onClick={onListItemClick(item.href)}
                >
                  {item.title}
                </a>
              ) : (
                <Link
                  key={item.href}
                  className="link-footer text-sm text-muted-foreground transition-colors duration-300 ease-in-out hover:text-foreground"
                  href={item.href}
                  onClick={onListItemClick(item.href)}
                >
                  {item.title}
                  {/* {item.icon ? <item.icon /> : null} */}
                </Link>
              ),
            )}
          </div>
        ))}
      </section>

      <section className="flex w-full flex-col p-2 pb-4 md:container md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-muted-foreground">
          &copy; {new Date().getFullYear()} {footerConfig.company.name}
        </p>
        <p className="text-sm text-muted-foreground">
          {footerConfig.company.address},<br />
          {footerConfig.company.country}
        </p>
      </section>
    </nav>
  );
}
